import * as THREE from "three";
import {
  Billboard,
  BillboardType,
  BILLBOARD_CONFIG,
} from "../../../../shared/dist/billboard.types";
import {
  EnvironmentElement,
  EnvironmentElementType,
} from "../../../../shared/dist/environment.types";
import { CLIENT_BILLBOARD_CONFIG } from "../config";
import { EventManager } from "../EventManager";

// Perf: face materials own a 1024x512 canvas texture each - must be disposed
// on replace/clear, otherwise every chunk rebuild leaks VRAM.
function disposeBillboardFaceMaterial(mesh: THREE.Mesh): void {
  const material = mesh.material as THREE.MeshStandardMaterial | THREE.MeshStandardMaterial[];
  const materials = Array.isArray(material) ? material : [material];
  for (const mat of materials) {
    // Shared geometries/materials (supports, face box) are never disposed here -
    // only the per-billboard face material with its canvas texture.
    if (mat === BillboardRenderer.getSupportMaterial()) {
      continue;
    }
    if ((mat as THREE.MeshStandardMaterial).map) {
      (mat as THREE.MeshStandardMaterial).map?.dispose();
    }
    mat.dispose();
  }
}

/**
 * Klasa odpowiedzialna za renderowanie billboardów reklamowych w grze
 */
export class BillboardRenderer {
  private scene: THREE.Scene;
  private billboards: Map<string, THREE.Group> = new Map();
  private billboardData: Map<string, Billboard> = new Map();
  private lastInteractionTime: Map<string, number> = new Map();
  private textureLoader: THREE.TextureLoader;
  private pendingRequests: Set<string> = new Set(); //to stop multiple requests for the same billboard
  // Perf: supports share one unit geometry (scaled per instance) + material.
  // Face geometry is constant-size, also shared.
  private static sharedSupportGeometry: THREE.BoxGeometry | null = null;
  private static sharedSupportMaterial: THREE.MeshStandardMaterial | null = null;
  private static sharedFaceGeometry: THREE.BoxGeometry | null = null;

  public static getSupportGeometry(): THREE.BoxGeometry {
    if (!BillboardRenderer.sharedSupportGeometry) {
      BillboardRenderer.sharedSupportGeometry = new THREE.BoxGeometry(0.3, 1, 0.3);
    }
    return BillboardRenderer.sharedSupportGeometry;
  }

  public static getSupportMaterial(): THREE.MeshStandardMaterial {
    if (!BillboardRenderer.sharedSupportMaterial) {
      BillboardRenderer.sharedSupportMaterial = new THREE.MeshStandardMaterial({ color: 0x555555 });
    }
    return BillboardRenderer.sharedSupportMaterial;
  }

  public static getFaceGeometry(): THREE.BoxGeometry {
    if (!BillboardRenderer.sharedFaceGeometry) {
      BillboardRenderer.sharedFaceGeometry = new THREE.BoxGeometry(
        CLIENT_BILLBOARD_CONFIG.DEFAULT_WIDTH,
        CLIENT_BILLBOARD_CONFIG.DEFAULT_HEIGHT,
        0.1,
      );
    }
    return BillboardRenderer.sharedFaceGeometry;
  }

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.textureLoader = new THREE.TextureLoader();

    // Inicjalizacja raycaster do obsługi kliknięć myszą
    this.setupMouseInteraction();
  }

  /**
   * Konfiguruje obsługę interakcji myszą z billboardami
   */
  private setupMouseInteraction(): void {
    // Tworzymy raycaster do wykrywania kliknięć
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    // Dodajemy listener na kliknięcie myszą
    EventManager.addListener('billboardRenderer', window, 'click', ((evt: Event) => {
      const event = evt as MouseEvent;
      // Sprawdzamy, czy mamy dostęp do sceny i kamery
      if (!this.scene.userData.camera) {
        return;
      }

      // Obliczamy pozycję myszy w przestrzeni normalizowanej (-1 do 1)
      mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
      mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

      // Aktualizujemy raycaster
      raycaster.setFromCamera(mouse, this.scene.userData.camera);

      // Pobieramy wszystkie meshe billboardów
      const billboardMeshes: THREE.Object3D[] = [];
      this.billboards.forEach((group) => {
        group.children.forEach((child) => {
          billboardMeshes.push(child);
        });
      });

      // Sprawdzamy przecięcia
      const intersects = raycaster.intersectObjects(billboardMeshes);

      if (intersects.length > 0) {
        // Znaleziono trafienie
        const hitObject = intersects[0].object;
        const billboardGroup = hitObject.parent;

        if (billboardGroup && (billboardGroup as any).billboardId) {
          const billboardId = (billboardGroup as any).billboardId;

          // Obsługujemy interakcję
          if (window.playerTank) {
            this.handleBillboardInteraction(
              billboardId,
              window.playerTank.playerId
            );
          }
        }
      }
    }));
  }

  /**
   * Tworzy lub aktualizuje billboard na podstawie danych z serwera
   * @param element Element środowiska reprezentujący billboard
   */
  public createOrUpdateBillboard(element: EnvironmentElement): void {
    // Pobieramy dane billboardu z serwera
    this.fetchBillboardData(element.id).then((billboardData) => {
      if (!billboardData) {
        return;
      }

      // Zapisujemy dane billboardu
      this.billboardData.set(element.id, billboardData);

      // Sprawdzamy, czy billboard już istnieje
      let billboardGroup = this.billboards.get(element.id);
      if (billboardGroup) {
        // Aktualizujemy istniejący billboard
        this.updateBillboard(billboardGroup, billboardData);
      } else {
        // Tworzymy nowy billboard
        billboardGroup = this.createBillboard(billboardData);
        this.billboards.set(element.id, billboardGroup);
        this.scene.add(billboardGroup);
      }

      // Aktualizujemy pozycję i rotację
      billboardGroup.position.copy(element.position);
      billboardGroup.rotation.copy(element.rotation);
    });
  }

  /**
   * Pobiera dane billboardu z serwera
   * @param billboardId ID billboardu
   * @returns Dane billboardu lub null w przypadku błędu
   */
  private async fetchBillboardData(
    billboardId: string
  ): Promise<Billboard | null> {
    let timeoutId: number | null = null;

    // Skip if we already have a pending request for this billboard (request are sent for every chunk)
    if (this.pendingRequests.has(billboardId)) {
      return null;
    }

    this.pendingRequests.add(billboardId);

    try {
      const cleanup = () => this.pendingRequests.delete(billboardId);

      // Wysyłamy zapytanie do serwera o dane billboardu
      return new Promise((resolve) => {
        if (window.socket) {
          // Use a specific event name that includes the billboard ID
          const responseEvent = `billboard:data:${billboardId}`;

          // Nasłuchujemy na odpowiedź z serwera
          const handleResponse = (data: Billboard | Billboard[]) => {

            // Anuluj timeout aby uniknąć wyścigu (race condition)
            if (timeoutId) {
              clearTimeout(timeoutId);
              timeoutId = null;
            }

            // Sprawdź czy dane przyszły jako tablica i wyciągnij pierwszy element
            const billboardData = Array.isArray(data) ? data[0] : data;

            window.socket?.off(responseEvent, handleResponse);
            cleanup();
            resolve(billboardData);
          }

          // Rejestrujemy nasłuchiwanie na odpowiedź
          window.socket.on(responseEvent, handleResponse);

          // Wysyłamy zapytanie
          window.socket.emit("billboard:request", { billboardId });

          // Timeout na wypadek braku odpowiedzi
          timeoutId = setTimeout(() => {
            window.socket?.off(responseEvent, handleResponse);
            cleanup();
            resolve(this.getFallbackBillboardData(billboardId));
          }, 10000);
        } else {
          // Brak połączenia z serwerem, używamy danych zastępczych
          resolve(this.getFallbackBillboardData(billboardId));
        }
      });
    } catch (error) {
      this.pendingRequests.delete(billboardId);
      return this.getFallbackBillboardData(billboardId);
    }
  }

  /**
   * Zwraca zastępcze dane billboardu w przypadku braku połączenia z serwerem
   * @param billboardId ID billboardu
   * @returns Zastępcze dane billboardu
   */
  private getFallbackBillboardData(billboardId: string): Billboard | null {
    // Dla pozostałych billboardów
    /*
        if (billboardId.startsWith('billboard_')) {
            return {
                id: billboardId,
                type: BillboardType.TEXT,
                position: new THREE.Vector3(0, 0, 0),
                rotation: new THREE.Euler(0, 0, 0),
                scale: new THREE.Vector3(
                    CLIENT_BILLBOARD_CONFIG.DEFAULT_WIDTH,
                    CLIENT_BILLBOARD_CONFIG.DEFAULT_HEIGHT,
                    1
                ),
                content: 'REKLAMA (OFFLINE)',
                backgroundColor: CLIENT_BILLBOARD_CONFIG.DEFAULT_BG_COLOR,
                textColor: CLIENT_BILLBOARD_CONFIG.DEFAULT_TEXT_COLOR,
                fontSize: CLIENT_BILLBOARD_CONFIG.DEFAULT_FONT_SIZE,
                interactive: false
            }
        }
            */

    return null;
  }

  /**
   * Tworzy nowy billboard na podstawie danych
   * @param billboard Dane billboardu
   * @returns Grupa THREE.js zawierająca billboard
   */
  private createBillboard(billboard: Billboard): THREE.Group {
    const group = new THREE.Group();
    group.name = `billboard_${billboard.id}`;

    // Używamy stałych wymiarów dla billboardów, aby zapewnić spójność między klientami
    const billboardWidth = CLIENT_BILLBOARD_CONFIG.DEFAULT_WIDTH;
    const billboardHeight = CLIENT_BILLBOARD_CONFIG.DEFAULT_HEIGHT;

    // Tworzymy podstawę billboardu (płaski prostokąt) - współdzielona geometria
    const geometry = BillboardRenderer.getFaceGeometry();

    let material: THREE.Material;

    if (billboard.type === BillboardType.TEXT) {
      // Billboard tekstowy
      material = this.createTextBillboardMaterial(billboard);
    } else {
      // Billboard z obrazem
      material = this.createImageBillboardMaterial(billboard);
    }

    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;

    // Dodajemy wsporniki (współdzielona geometria jednostkowa skalowana per wysokość)
    // Obliczamy wysokość wsporników na podstawie pozycji Y billboardu
    // Wsporniki powinny sięgać od środka billboardu do ziemi plus dodatkowe 0.5 jednostki pod ziemię
    const supportHeight = billboard.position.y + billboardHeight * 0.5 + 0.5;
    const supportGeometry = BillboardRenderer.getSupportGeometry();
    const supportMaterial = BillboardRenderer.getSupportMaterial();

    const leftSupport = new THREE.Mesh(supportGeometry, supportMaterial);
    leftSupport.scale.y = supportHeight;
    // Pozycjonujemy wspornik tak, aby jego górna część była przy dolnej krawędzi billboardu
    leftSupport.position.set(
      -billboardWidth / 2,
      -billboardHeight * 0.5 - supportHeight * 0.5,
      0
    );
    leftSupport.castShadow = true;
    leftSupport.receiveShadow = true;

    const rightSupport = new THREE.Mesh(supportGeometry, supportMaterial);
    rightSupport.scale.y = supportHeight;
    rightSupport.position.set(
      billboardWidth / 2,
      -billboardHeight * 0.5 - supportHeight * 0.5,
      0
    );
    rightSupport.castShadow = true;
    rightSupport.receiveShadow = true;

    // Dodajemy elementy do grupy
    group.add(mesh);
    group.add(leftSupport);
    group.add(rightSupport);

    // Dodajemy właściwość do identyfikacji billboardu przy raycasting
    (group as any).billboardId = billboard.id;

    return group;
  }

  /**
   * Aktualizuje istniejący billboard na podstawie nowych danych
   * @param billboardGroup Grupa THREE.js zawierająca billboard
   * @param billboard Nowe dane billboardu
   */
  private updateBillboard(
    billboardGroup: THREE.Group,
    billboard: Billboard
  ): void {
    // Używamy stałych wymiarów dla billboardów, aby zapewnić spójność między klientami
    const billboardWidth = CLIENT_BILLBOARD_CONFIG.DEFAULT_WIDTH;
    const billboardHeight = CLIENT_BILLBOARD_CONFIG.DEFAULT_HEIGHT;

    // Aktualizujemy materiał billboardu
    const mesh = billboardGroup.children[0] as THREE.Mesh;

    if (mesh) {
      // Perf: dispose poprzedniej tekstury canvas (VRAM leak przy każdym update)
      disposeBillboardFaceMaterial(mesh);

      if (billboard.type === BillboardType.TEXT) {
        mesh.material = this.createTextBillboardMaterial(billboard);
      } else {
        mesh.material = this.createImageBillboardMaterial(billboard);
      }

      // Wymiary są stałe - współdzielona geometria, brak realokacji
      mesh.geometry = BillboardRenderer.getFaceGeometry();
    }

    // Aktualizujemy wsporniki
    const supportHeight = billboard.position.y + billboardHeight * 0.5 + 0.5;

    if (billboardGroup.children.length >= 3) {
      const leftSupport = billboardGroup.children[1] as THREE.Mesh;
      const rightSupport = billboardGroup.children[2] as THREE.Mesh;

      if (leftSupport && rightSupport) {
        // Współdzielona geometria jednostkowa - tylko skala
        leftSupport.geometry = BillboardRenderer.getSupportGeometry();
        rightSupport.geometry = BillboardRenderer.getSupportGeometry();
        leftSupport.scale.y = supportHeight;
        rightSupport.scale.y = supportHeight;

        // Aktualizujemy pozycje wsporników
        leftSupport.position.set(
          -billboardWidth / 2,
          -billboardHeight * 0.5 - supportHeight * 0.5,
          0
        );

        rightSupport.position.set(
          billboardWidth / 2,
          -billboardHeight * 0.5 - supportHeight * 0.5,
          0
        );
      }
    }

    // Aktualizujemy właściwość billboardId
    (billboardGroup as any).billboardId = billboard.id;
  }

  /**
   * Tworzy materiał dla billboardu tekstowego
   * @param billboard Dane billboardu
   * @returns Materiał THREE.js
   */
  private createTextBillboardMaterial(billboard: Billboard): THREE.Material {
    // Tworzymy canvas do renderowania tekstu
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    if (!context) {
      console.error(
        "[BillboardRenderer] Nie udało się utworzyć kontekstu canvas"
      );
      return new THREE.MeshBasicMaterial({ color: 0xff0000 });
    }

    // Ustawiamy rozmiar canvas proporcjonalnie do billboardu
    canvas.width = 1024;
    canvas.height = 512;

    // Wypełniamy tło
    context.fillStyle =
      billboard.backgroundColor || CLIENT_BILLBOARD_CONFIG.DEFAULT_BG_COLOR;
    context.fillRect(0, 0, canvas.width, canvas.height);

    // Dodajemy tekst
    const fontSize = billboard.fontSize ? billboard.fontSize * 100 : 50;
    context.font = `bold ${fontSize}px Arial, sans-serif`;
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillStyle =
      billboard.textColor || CLIENT_BILLBOARD_CONFIG.DEFAULT_TEXT_COLOR;

    // Obsługa wielu linii tekstu (podział po znaku \n)
    const lines = billboard.content.split("\n");
    const lineHeight = fontSize * 1.2; // Wysokość linii (120% rozmiaru czcionki)

    // Obliczamy pozycję startową dla wyśrodkowania wszystkich linii
    const startY = canvas.height / 2 - (lineHeight * (lines.length - 1)) / 2;

    // Rysujemy każdą linię osobno
    lines.forEach((line, index) => {
      const y = startY + index * lineHeight;
      context.fillText(line, canvas.width / 2, y);
    });

    // Dodajemy informację o interaktywności
    if (billboard.interactive && billboard.url) {
      context.font = `${fontSize * 0.4}px Arial, sans-serif`;
      context.fillText(
        "(Mouse click to open)",
        canvas.width / 2,
        canvas.height * 0.8
      );

      // Dodajemy celownik na środku billboardu
      const targetSize = fontSize * CLIENT_BILLBOARD_CONFIG.TARGET_SIZE_FACTOR;

      // Zewnętrzny okrąg celownika
      context.beginPath();
      context.arc(
        canvas.width / 2,
        canvas.height / 2,
        targetSize,
        0,
        Math.PI * 2
      );
      context.strokeStyle = "#ff0000";
      context.lineWidth = 3;
      context.stroke();

      // Wewnętrzny okrąg celownika
      context.beginPath();
      context.arc(
        canvas.width / 2,
        canvas.height / 2,
        targetSize * 0.5,
        0,
        Math.PI * 2
      );
      context.fillStyle = "#ff0000";
      context.fill();

      // Linie krzyżyka
      context.beginPath();
      context.moveTo(canvas.width / 2 - targetSize * 1.5, canvas.height / 2);
      context.lineTo(canvas.width / 2 + targetSize * 1.5, canvas.height / 2);
      context.moveTo(canvas.width / 2, canvas.height / 2 - targetSize * 1.5);
      context.lineTo(canvas.width / 2, canvas.height / 2 + targetSize * 1.5);
      context.strokeStyle = "#ff0000";
      context.lineWidth = 2;
      context.stroke();
    }

    // Tworzymy teksturę z canvas
    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;

    // Tworzymy materiał z teksturą
    return new THREE.MeshStandardMaterial({
      map: texture,
      roughness: 0.5,
      metalness: 0.1,
    });
  }

  /**
   * Tworzy materiał dla billboardu z obrazem
   * @param billboard Dane billboardu
   * @returns Materiał THREE.js
   */
  private createImageBillboardMaterial(billboard: Billboard): THREE.Material {
    // Domyślny materiał w przypadku błędu
    const defaultMaterial = new THREE.MeshStandardMaterial({ color: 0xcccccc });

    // Ładujemy teksturę z URL
    this.textureLoader.load(
      billboard.content,
      (texture) => {
        // Sukces - aktualizujemy materiał billboardu
        const material = new THREE.MeshStandardMaterial({
          map: texture,
          roughness: 0.5,
          metalness: 0.1
        });

        // Znajdujemy billboard i aktualizujemy jego materiał
        const billboardGroup = this.billboards.get(billboard.id);
        if (billboardGroup) {
          const mesh = billboardGroup.children[0] as THREE.Mesh;
          if (mesh) {
            mesh.material = material;
          }
        }
      },
      undefined,
      (error) => {
        console.error(
          `[BillboardRenderer] Błąd ładowania tekstury dla billboardu ${billboard.id}:`,
          error
        );
      }
    );

    return defaultMaterial;
  }

  /**
   * Obsługuje interakcję z billboardem
   * @param billboardId ID billboardu
   * @param playerId ID gracza
   */
  public handleBillboardInteraction(
    billboardId: string,
    playerId: string
  ): void {
    // Sprawdzamy, czy billboard istnieje
    const billboardData = this.billboardData.get(billboardId);

    // Sprawdzamy, czy billboard jest interaktywny i ma URL
    if (!billboardData || !billboardData.interactive || !billboardData.url) {
      console.log(
        `[BillboardRenderer] Billboard ${billboardId} nie jest interaktywny lub nie ma URL`
      );
      return;
    }

    // Sprawdzamy, czy nie ma cooldownu na interakcję
    const now = Date.now();
    const lastInteraction = this.lastInteractionTime.get(billboardId) || 0;

    if (now - lastInteraction < CLIENT_BILLBOARD_CONFIG.CLICK_COOLDOWN) {
      return;
    }

    // Aktualizujemy czas ostatniej interakcji
    this.lastInteractionTime.set(billboardId, now);

    // Dodajemy efekt wizualny interakcji
    this.showInteractionEffect(billboardId);

    // Otwieramy URL w nowej karcie
    window.open(billboardData.url, "_blank");

    // Emitujemy event interakcji
    const event = new CustomEvent("billboard:interaction", {
      detail: { billboardId, playerId },
    });
    document.dispatchEvent(event);
  }

  /**
   * Pokazuje efekt wizualny interakcji z billboardem
   * @param billboardId ID billboardu
   */
  private showInteractionEffect(billboardId: string): void {
    const billboard = this.billboards.get(billboardId);
    if (!billboard) return;

    // Zapisujemy oryginalny kolor
    const originalMaterial = (billboard.children[0] as THREE.Mesh)
      .material as THREE.MeshBasicMaterial;
    const originalColor = originalMaterial.color.clone();

    // Zmieniamy kolor na jaśniejszy
    originalMaterial.color.set(0xffffff);

    // Przywracamy oryginalny kolor po określonym czasie
    setTimeout(() => {
      originalMaterial.color.copy(originalColor);
    }, CLIENT_BILLBOARD_CONFIG.INTERACTION_EFFECT_DURATION);
  }

  /**
   * Wysyła informację o interakcji z billboardem do serwera
   * @param billboardId ID billboardu
   * @param playerId ID gracza
   */
  private sendInteractionToServer(billboardId: string, playerId: string): void {
    // Przykład emisji eventu do serwera (do zaimplementowania w network.ts)
    const event = new CustomEvent("billboard:interaction", {
      detail: { billboardId, playerId },
    });
    document.dispatchEvent(event);
  }

  /**
   * Sprawdza, czy pocisk trafił w billboard
   * @param position Pozycja pocisku
   * @param direction Kierunek pocisku
   * @returns ID billboardu lub null
   * @deprecated Ta metoda nie jest już używana, ponieważ interakcja z billboardami odbywa się tylko poprzez kliknięcia myszką
   */
  public checkBillboardHit(
    position: THREE.Vector3,
    direction: THREE.Vector3
  ): string | null {
    // Metoda została uproszczona, ponieważ nie jest już używana
    return null;
  }

  /**
   * Usuwa wszystkie billboardy
   */
  public clearAllBillboards(): void {
    this.billboards.forEach((group) => {
      const face = group.children[0] as THREE.Mesh | undefined;
      if (face) {
        disposeBillboardFaceMaterial(face);
      }
      this.scene.remove(group);
    });

    this.billboards.clear();
    this.billboardData.clear();
  }
}
